<template>
  <v-card class="medidores-card" elevation="1" height="100%">
    <div class="medidores-card__header">
      <div class="medidores-card__title">Contadores</div>
      <div class="medidores-card__results">{{ resultsLabel }}</div>
    </div>

    <div class="medidores-card__search">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar contador"
        solo
        flat
        clearable
        hide-details
        background-color="white"
        class="medidores-search"
      />
    </div>

    <div v-if="filteredItems.length" class="medidores-list">
      <button
        v-for="item in filteredItems"
        :key="item.id"
        type="button"
        :class="['medidor-item', { 'medidor-item--active': selectedItem && selectedItem.id === item.id }]"
        @click="$emit('select', item)"
      >
        <v-avatar class="medidor-item__avatar" size="42">
          <v-icon color="teal darken-2" size="20">mdi-waves</v-icon>
        </v-avatar>

        <div class="medidor-item__copy">
          <div class="medidor-item__title">{{ item.nombre }}</div>
          <div class="medidor-item__subtitle">{{ item.subtitle }}</div>
        </div>

        <div class="medidor-item__values">
          <div class="medidor-item__value-main">{{ item.primaryValue }}</div>
          <div v-if="item.secondaryValue" class="medidor-item__value-sub">{{ item.secondaryValue }}</div>
        </div>
      </button>
    </div>

    <v-card-text v-else class="text-center py-10">
      <v-avatar color="grey lighten-4" size="56" class="mb-4">
        <v-icon color="grey lighten-1" size="28">mdi-water-off-outline</v-icon>
      </v-avatar>
      <div class="subtitle-1 font-weight-medium">No se encontraron contadores</div>
      <div class="body-2 text--secondary mt-1">
        Ajusta la búsqueda para ver más resultados.
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
function formatWithUnit(value, decimals, unit) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return unit ? `${parsed.toFixed(decimals)} ${unit}` : parsed.toFixed(decimals);
}

function buildCaudalGroups(sensores) {
  const groups = new Map();
  for (const sensor of sensores) {
    const key = sensor.id_remote ?? sensor.id;
    if (!groups.has(key)) {
      groups.set(key, { key, id_remote: sensor.id_remote ?? null, sensors: [], inst: null, acum: null });
    }
    const group = groups.get(key);
    group.sensors.push(sensor);
    if (Number(sensor.id_tipo_sensor) === 26) group.inst = sensor;
    if (Number(sensor.id_tipo_sensor) === 27) group.acum = sensor;
  }
  return [...groups.values()];
}

function sortByName(a, b) {
  return (a.nombre ?? '').localeCompare(b.nombre ?? '', 'es', { sensitivity: 'base' });
}

export default {
  name: 'ContadoresList',
  props: {
    contadores:    { type: Array,  required: true },
    caudalSensores:{ type: Array,  default: () => [] },
    selectedItem:  { type: Object, default: null },
  },
  data() {
    return { search: '' };
  },
  computed: {
    allItems() {
      const contadorItems = this.contadores.map((contador) => {
        const flow = formatWithUnit(contador?.caudal, 2, 'm³/h');
        const remoteLabel = contador?.id_remote ?? 'Sin remote';
        const fechaLabel = contador?.fecha_registro
          ? new Date(contador.fecha_registro).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
          : null;
        return {
          id: `c_${contador.id}`,
          nombre: contador?.nombre ?? 'Contador',
          subtitle: remoteLabel,
          primaryValue: flow ?? fechaLabel ?? '-',
          secondaryValue: flow ? fechaLabel : null,
          searchableText: `${contador?.nombre ?? ''} ${remoteLabel}`.toLowerCase(),
          raw: { kind: 'contador', data: contador },
        };
      }).sort(sortByName);

      const caudalItems = buildCaudalGroups(this.caudalSensores).map((group) => {
        const instantValue = formatWithUnit(group.inst?.value, 2, group.inst?.unidades);
        const accumulatedValue = formatWithUnit(group.acum?.value, 2, group.acum?.unidades);
        const remoteLabel = group.id_remote ?? 'Sin remote';
        const sensorsLabel = `${group.sensors.length} ${group.sensors.length === 1 ? 'sensor' : 'sensores'}`;
        return {
          id: `caudal_${group.key}`,
          nombre: group.id_remote ?? 'Caudal',
          subtitle: sensorsLabel,
          primaryValue: instantValue ?? accumulatedValue ?? 'Sin dato',
          secondaryValue: accumulatedValue ?? sensorsLabel,
          searchableText: `${remoteLabel} ${sensorsLabel}`.toLowerCase(),
          raw: { kind: 'caudal', remoteId: group.id_remote ?? null, sensors: group.sensors },
        };
      }).sort(sortByName);

      return [...contadorItems, ...caudalItems];
    },

    filteredItems() {
      const query = (this.search ?? '').trim().toLowerCase();
      if (!query) return this.allItems;
      return this.allItems.filter((item) => item.searchableText.includes(query));
    },

    resultsLabel() {
      const total = this.allItems.length;
      const filtered = this.filteredItems.length;
      if (total === 1) return '1 contador';
      if (filtered === total) return `${total} contadores`;
      return `${filtered} de ${total} contadores`;
    },
  },
};
</script>

<style scoped>
.medidores-card {
  background: rgba(255, 255, 255, 0.97);
}

.medidores-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 10px;
}

.medidores-card__title {
  font-size: 20px;
  /* font-weight: 700; */
  color: #0f172a;
}

.medidores-card__results {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.medidores-card__search {
  padding: 0 18px 10px;
}

.medidores-list {
  display: grid;
  gap: 10px;
  padding: 0 12px 14px;
}

.medidor-item {
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: white;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
  border-radius: 8px;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease,
    background-color 0.18s ease;
}

.medidor-item:hover {
  transform: translateY(-1px);
  border-color: rgba(13, 148, 136, 0.24);
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
}

.medidor-item--active {
  background: rgba(240, 253, 250, 0.72);
  border-color: #14b8a6;
  box-shadow: 0 18px 32px rgba(13, 148, 136, 0.12);
}

.medidor-item__avatar {
  background: rgba(240, 253, 250, 0.94) !important;
}

.medidor-item__copy { min-width: 0; }

.medidor-item__title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}

.medidor-item__subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.medidor-item__values {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  gap: 4px;
  min-width: 112px;
}

.medidor-item__value-main {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f766e;
}

.medidor-item__value-sub {
  font-size: 12px;
  color: #64748b;
}

@media (max-width: 600px) {
  .medidor-item {
    grid-template-columns: auto minmax(0, 1fr);
  }
  .medidor-item__values {
    grid-column: 2;
    align-items: flex-start;
    text-align: left;
    min-width: 0;
  }
}
</style>
